import { PHASE_STEP, PHASES, type DiscoveriesDto, type PhaseNumber, type StepTimelineDto, type TimelineEntryDto, type WizardStepDto } from '@bk/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { APP_NAME } from '../../lib/app-name';
import './sinapsi.css';

/**
 * I pezzi comuni dello stile «Sinapsi» (docs/mockup/percorso-sinapsi): la pagina scura con le luci, la testata
 * «Timonio · rete neurale di <azienda>», i neuroni, la catena delle fasi, il lettore e il contatore, i fatti con la fonte.
 */

/** I caratteri dello stile, da Google Fonts, una volta sola; senza rete restano quelli di sistema. */
export function useSinapsiFonts() {
  useEffect(() => {
    if (document.getElementById('sn-fonts')) return;
    const l = document.createElement('link');
    l.id = 'sn-fonts';
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Unbounded:wght@500;700;800&family=Manrope:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';
    document.head.appendChild(l);
  }, []);
}

/** Minuti e secondi: «0:47». */
export const clock = (s: number) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, Math.round(s)) % 60).padStart(2, '0')}`;

/** L'ora che scorre, un secondo alla volta, finché serve. */
export function useNow(on = true): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return now;
}

const ms = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : null);
/** Quanto è durato (o dura) un tratto, in secondi. */
export const spent = (a: string | null | undefined, b: string | null | undefined, now: number) => {
  const s = ms(a);
  return s === null ? 0 : ((ms(b) ?? now) - s) / 1000;
};

/** La pagina intera nello stile: sfondo, luci, testata e contenuto. `glow` sceglie dove stanno le luci. */
export function SinapsiPage({
  glow = 'a',
  full = true,
  network,
  state,
  stateTone = 'ok',
  tools,
  onExit,
  children,
}: {
  glow?: 'a' | 'b' | 'c' | 'd';
  /** A tutta finestra (fuori dalla cornice dell'app). */
  full?: boolean;
  /** «RETE NEURALE DELLA TUA AZIENDA» o «RETE NEURALE DI BOOSTAR». */
  network: string;
  /** Lo stato in alto a destra: «CONTRATTO 1 · V2.0», «FASE 0 · IDENTITÀ»… */
  state: ReactNode;
  stateTone?: 'ok' | 'on' | 'gold';
  /** I comandi del titolare sul percorso (ADR 0172), tra lo stato e l'uscita. */
  tools?: ReactNode;
  /** Un modo per uscire dalla pagina piena (le aziende, le impostazioni). */
  onExit?: { label: string; go: () => void };
  children: ReactNode;
}) {
  useSinapsiFonts();
  return (
    <div className={`sn ${full ? 'sn-full' : ''}`}>
      <div aria-hidden className={`sn-glow ${glow}`} />
      <header className="sn-head">
        <span className="logo">{APP_NAME}</span>
        <span className="sn-ey">{network}</span>
        <span className="state">
          <span className={`sn-dot ${stateTone} ${stateTone === 'on' ? '' : ''}`} style={{ width: 8, height: 8 }} />
          {state}
        </span>
        {tools}
        {onExit && (
          <button type="button" className="exit" onClick={onExit.go}>
            {onExit.label}
          </button>
        )}
      </header>
      {children}
    </div>
  );
}

/** La catena orizzontale delle tappe: fatte, quella accesa, quelle dopo. */
export function Chain({ steps, at }: { steps: string[]; at: number }) {
  return (
    <div>
      <div className="sn-chain">
        {steps.map((_, k) => (
          <span key={k} style={{ display: 'contents' }}>
            <span className={`sn-dot ${k < at ? 'ok' : k === at ? 'on big' : 'off'}`} style={k < at ? { width: 14, height: 14, boxShadow: '0 0 14px #46e0b0' } : k > at ? { width: 12, height: 12 } : undefined} />
            {k < steps.length - 1 && <span className={`line ${k < at - 1 ? 'done' : k === at - 1 ? 'done' : k === at ? 'lit' : ''}`} />}
          </span>
        ))}
      </div>
      <div className="sn-chain-labels">
        {steps.map((s) => (
          <span key={s}>{s}</span>
        ))}
      </div>
    </div>
  );
}

/** Le fasi del contratto n. 1 (art. 8) e il passo della configurazione in cui vivono: dal registro unico `PHASES`. */
export const FASI: ReadonlyArray<{ n: number; titolo: string; passo: WizardStepDto['chiave'] | null }> = PHASES.map((p) => ({ n: p.n, titolo: p.titolo, passo: p.passo }));

/**
 * La catena delle fasi a sinistra (03-fase0): neurone acceso = fase aperta, verde = fatta con il livello o il tempo,
 * bordo ciano = si prepara, spento = dopo. Sotto, le domande aperte dell'incarico.
 */
export function PhaseNav({
  steps,
  current,
  levels = {},
  open = 0,
  chosen = 0,
  perPhase = null,
  onRegistry,
  go,
}: {
  steps: WizardStepDto[];
  current: WizardStepDto['chiave'] | null;
  /** Il livello di una fase già calcolato (es. «3/5»). */
  levels?: Partial<Record<number, string>>;
  open?: number;
  /** Le scelte di Timonio del registro (art. 7-bis.2). */
  chosen?: number;
  /** «1 della fase 0, 2 di questa fase»: le aperte per fase. */
  perPhase?: string | null;
  /** Apre il registro intero delle domande (art. 7-bis.11). */
  onRegistry?: () => void;
  go?: (k: WizardStepDto['chiave']) => void;
}) {
  const now = useNow(true);
  const order = steps.map((s) => s.chiave);
  const at = current ? order.indexOf(current) : -1;
  return (
    <nav aria-label="Le fasi" className="sn-nav">
      <span aria-hidden className="rail" />
      <p className="sn-ey ttl">La tua azienda</p>
      {FASI.map((f) => {
        const step = f.passo ? steps.find((s) => s.chiave === f.passo) : null;
        const i = step ? order.indexOf(step.chiave) : -1;
        const here = !!step && step.chiave === current;
        const done = !!step && i >= 0 && i < at;
        const phase = !!step && PHASE_STEP[f.n as PhaseNumber] === step.chiave && f.n > 0;
        const preparing = !!step && !here && !done && !step.consegnata && (phase ? !step.fermato : step.preparando || (!!step.tempi && !step.tempi.fine));
        // Una fase dopo quella di adesso si dice «pronta» solo con la consegna salvata (si apre con «Avanti»); senza
        // consegna «si prepara», anche se un suo giro del contatore è chiuso (art. 8: aspetta le fasi che eredita).
        const ready = !!step && !here && !done && !!step.consegnata;
        const cls = here ? 'here' : done ? 'done' : preparing || ready ? 'near' : '';
        // La fase 0 conta solo dalla conferma dell'identità (art. 10.1): un giro senza quel segno non è il suo tempo.
        const time = step?.tempi && (f.n !== 0 || step.tempi.conteggio_da) ? clock(spent(step.tempi.conteggio_da ?? step.tempi.inizio, step.tempi.fine, now)) : null;
        const label = here ? [levels[f.n], time].filter(Boolean).join(' · ') : done ? [levels[f.n], time].filter(Boolean).join(' · ') : preparing ? 'si prepara' : ready ? 'pronta' : '';
        const dot = here ? 'on' : done ? 'ok' : preparing || ready ? 'ready' : 'faint';
        const body = (
          <>
            <span className={`sn-dot ${dot}`} style={here ? { width: 14, height: 14, boxShadow: '0 0 18px #3cf0ff' } : undefined} />
            <span>
              {f.n} {f.titolo}
            </span>
            <span className="t">{label}</span>
          </>
        );
        return done && go && step ? (
          <a key={f.n} href={`#${step.chiave}`} className={`row ${cls}`} onClick={(e) => e.preventDefault()}>
            {body}
          </a>
        ) : (
          <span key={f.n} className={`row ${cls}`} aria-current={here ? 'page' : undefined}>
            {body}
          </span>
        );
      })}
      <div className="open">
        <span className="sn-ey">Domande aperte · {open}</span>
        <p style={{ marginBottom: 4 }}>Scelte di Timonio: {chosen}</p>
        <p>{perPhase ? `${perPhase}. Rispondi quando vuoi.` : 'Rispondi quando vuoi: niente aspetta le tue risposte.'}</p>
        {onRegistry && (
          <button type="button" className="sn-link" style={{ fontSize: 13, marginTop: 4 }} onClick={onRegistry}>
            Il registro intero delle domande
          </button>
        )}
      </div>
    </nav>
  );
}

/** Il livello a cinque barrette (art. 9.3). */
export function Level({ value }: { value: number }) {
  return (
    <span className="sn-level" aria-label={`Livello ${value} su 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i key={n} className={n <= value ? 'on' : ''} />
      ))}
    </span>
  );
}

function RowDot({ e, sub }: { e: TimelineEntryDto['esito']; sub: boolean }) {
  if (sub) return <span className="stem" />;
  if (e === 'fatto') return <span className="sn-dot ok" />;
  if (e === 'errore') return <span className="sn-dot red" />;
  if (e === 'in_corso') return <span className="sn-dot on" style={{ width: 12, height: 12, boxShadow: '0 0 16px #3cf0ff' }} />;
  return <span className="sn-dot off" />;
}

function Row({ v, now, sub }: { v: TimelineEntryDto; now: number; sub: boolean }) {
  const s = spent(v.inizio, v.fine, now);
  const time = v.esito === 'previsto' ? (v.previsto_s ? `~${clock(v.previsto_s)}` : '') : clock(s);
  const tone = v.esito === 'fatto' ? 'ok' : v.esito === 'errore' ? 'err' : v.esito === 'in_corso' ? 'now' : 'todo';
  return (
    <>
      <RowDot e={v.esito} sub={sub} />
      <span className={`lbl ${sub ? 'sub' : ''} ${v.esito === 'in_corso' ? 'now' : v.esito === 'previsto' ? 'todo' : ''}`}>
        {v.etichetta}
        {v.esito === 'errore' && v.causa ? ` · non riuscita: ${v.causa}` : ''}
      </span>
      <span className={`t ${tone}`}>{time}</span>
    </>
  );
}

/**
 * Il lettore e il contatore (art. 10.4): ogni attività con il suo stato e il suo tempo; in alto il totale, contato da
 * `conteggio_da` quando c'è (la fase 0 dalla conferma dell'identità), accanto al tempo massimo, in rosso se superato.
 */
export function Timeline({ t, title, eyebrow, note }: { t: StepTimelineDto; title: string; eyebrow: string; note?: string }) {
  const now = useNow(!t.fine);
  const total = spent(t.conteggio_da ?? t.inizio, t.fine, now);
  const over = total > t.massimo_s;
  const top = t.voci.filter((v) => !v.padre || !t.voci.some((x) => x.id === v.padre));
  return (
    <section className="sn-panel" style={{ padding: '26px 30px', gap: 14 }} role="status" aria-live="polite">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <p className="sn-ey cyan">{eyebrow}</p>
          <h1 className="sn-h1 sm" style={{ marginTop: 8 }}>
            {title}
          </h1>
        </div>
        <div className="sn-timer">
          <p className={`big ${over ? 'over' : ''}`}>{clock(total)}</p>
          <p className="max">Tempo massimo {clock(t.massimo_s)}</p>
        </div>
      </div>
      <div className="sn-bar">
        <i className={over ? 'over' : ''} style={{ width: `${Math.min(100, (total / t.massimo_s) * 100)}%` }} />
      </div>
      <div className="sn-rows">
        {top.map((v) => (
          <span key={v.id} style={{ display: 'contents' }}>
            <Row v={v} now={now} sub={false} />
            {t.voci
              .filter((x) => x.padre === v.id)
              .map((x) => (
                <Row key={x.id} v={x} now={now} sub />
              ))}
          </span>
        ))}
      </div>
      <p className="sn-note">{note ?? (over ? 'Oltre il tempo massimo: lo segno per migliorarlo. Puoi lasciare la pagina: la rete continua a lavorare.' : 'Puoi lasciare la pagina: la rete continua a lavorare.')}</p>
    </section>
  );
}

const GRADE_TONE: Record<string, string> = { A: 'on', B: 'violet', C: 'gold', D: 'gold', T: 'ok' };

/** I fatti già trovati, con la fonte e il grado: «Neuroni accesi · N fatti con la fonte». */
export function Neurons({ tenantId, live = true, max = 6 }: { tenantId: string; live?: boolean; max?: number }) {
  const list = useQuery({
    queryKey: ['tenant', tenantId, 'discoveries'],
    queryFn: () => api<DiscoveriesDto>(`/tenants/${tenantId}/onboarding/discoveries`),
    refetchInterval: live ? 3000 : false,
  });
  const facts = (list.data?.scoperte ?? []).filter((d) => d.grado);
  const n = list.data?.totale ?? facts.length;
  return (
    <aside className="sn-aside">
      <p className="sn-ey">
        Neuroni accesi · {n} {n === 1 ? 'fatto' : 'fatti'} con la fonte
      </p>
      {facts.slice(0, max).map((d) => {
        const [k, ...rest] = d.testo.split(': ');
        const label = rest.length ? k : null;
        const value = rest.length ? rest.join(': ') : d.testo;
        const tone = GRADE_TONE[d.grado ?? 'D'] ?? 'gold';
        const num = /^\d+$/.test(value.trim());
        return (
          <div key={d.chiave} className="sn-card sn-fade">
            <span aria-hidden className={`sn-dot mark ${tone}`} />
            {label && <p className="k">{label}</p>}
            <p className={`v ${num ? 'n' : ''}`}>{value}</p>
            <p className={`src ${tone === 'violet' ? 'violet' : tone === 'gold' ? 'gold' : ''}`}>
              {d.fonte}
              {d.grado ? ` · ${d.grado}` : ''}
            </p>
          </div>
        );
      })}
      {!facts.length && <p className="sn-note">Ancora nessun fatto: compaiono qui appena sono letti, con la fonte.</p>}
    </aside>
  );
}
